import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaChevronDown,
  FaChevronUp,
  FaClipboardList,
  FaCopy,
  FaCreditCard,
  FaEllipsisV,
  FaEnvelope,
  FaFolder,
  FaHome,
  FaLock,
  FaPhone,
  FaSearch,
  FaShieldAlt,
  FaSyncAlt,
  FaTrash,
} from "react-icons/fa";
import Spinner from "../components/Spinner";
import Modal from "../components/Modal";
import { LdsOverviewHero, LdsOverviewPage } from "../components/lds/LdsOverview";
import http from "../api/apiClient";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";
import { parseToDate } from "../utils/dateUtils";
import { useCan } from "../authz/useCan";

const parseFieldChanges = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (e) {
      return null;
    }
  }
  return null;
};

const normalizeChangeValue = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (typeof v === "object") {
    try {
      return JSON.stringify(v, null, 2);
    } catch (e) {
      // fall through
    }
  }
  try {
    return JSON.stringify(v);
  } catch (e) {
    return String(v);
  }
};

const buildChangeRows = (data: any) => {
  if (!data) return [];

  const asObjectDiff = (beforeObj, afterObj) => {
    if (!beforeObj && !afterObj) return [];
    const keys = Array.from(new Set([...(Object.keys(beforeObj || {})), ...(Object.keys(afterObj || {}))]));
    return keys
      .map((key) => {
        const prev = beforeObj ? beforeObj[key] : undefined;
        const next = afterObj ? afterObj[key] : undefined;
        const prevStr = JSON.stringify(prev);
        const nextStr = JSON.stringify(next);
        if (prevStr === nextStr) return null;
        return { field: key, prev, next };
      })
      .filter(Boolean);
  };

  const looksLikeBeforeAfterWrapper = (obj) => {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return false;
    const keys = Object.keys(obj);
    if (keys.length < 2) return false;
    const hasBefore = Object.prototype.hasOwnProperty.call(obj, "before");
    const hasAfter = Object.prototype.hasOwnProperty.call(obj, "after");
    if (!hasBefore || !hasAfter) return false;
    // If there are many other keys besides before/after, it's likely a wrapper payload.
    const otherKeys = keys.filter((k) => k !== "before" && k !== "after");
    return otherKeys.length > 0;
  };

  if (data && typeof data === "object" && !Array.isArray(data)) {
    // Common shape: { before: {...}, after: {...} }
    if (data.before || data.after || data.old || data.previous || data.prev || data.new || data.next || data.current) {
      const beforeObj = data.before || data.old || data.previous || data.prev;
      const afterObj = data.after || data.new || data.next || data.current;
      if (beforeObj || afterObj) return asObjectDiff(beforeObj || {}, afterObj || {});
    }

    // Backend sometimes wraps fields as: { before: {...}, after: {...}, ...same-fields-again }
    // In that case, show a single "before/after" block instead of a huge one-liner JSON.
    if (looksLikeBeforeAfterWrapper(data)) {
      return [{ field: "record", prev: data.before, next: data.after }];
    }
  }

  const rows = [];
  if (Array.isArray(data)) {
    data.forEach((item, idx) => {
      if (!item || typeof item !== "object") return;
      const field = item.field || item.name || item.key || `field_${idx}`;
      const prev =
        item.oldValue ??
        item.before ??
        item.old ??
        item.previous ??
        item.from ??
        item.prev ??
        item.valueBefore;
      const next =
        item.newValue ??
        item.after ??
        item.value ??
        item.new ??
        item.next ??
        item.to ??
        item.valueAfter;
      if (prev === undefined && next === undefined) return;
      rows.push({ field, prev, next });
    });
  } else if (typeof data === "object") {
    Object.entries(data).forEach(([field, val]) => {
      const change = val as any;
      if (change && typeof change === "object" && !Array.isArray(change)) {
        const prev =
          change.oldValue ??
          change.before ??
          change.old ??
          change.previous ??
          change.from ??
          change.prev ??
          change.was ??
          change.valueBefore;
        const next =
          change.newValue ??
          change.after ??
          change.value ??
          change.new ??
          change.next ??
          change.to ??
          change.is ??
          change.valueAfter ??
          change.now;
        if (prev === undefined && next === undefined) return;
        rows.push({ field, prev, next });
      } else {
        rows.push({ field, prev: val, next: undefined });
      }
    });
  }
  return rows;
};

const looksLikeUuid = (value) => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(
    trimmed
  );
};

const normalizePage = (data) => {
  if (!data) return { items: [], totalPages: 1, page: 0, size: 50, totalElements: 0 };
  if (Array.isArray(data)) return { items: data, totalPages: 1, page: 0, size: data.length || 50, totalElements: data.length };
  const content = Array.isArray(data.content)
    ? data.content
    : Array.isArray(data.items)
    ? data.items
    : Array.isArray(data.data)
    ? data.data
    : [];
  const totalPages = Number.isFinite(data.totalPages) ? data.totalPages : 1;
  const totalElements = Number.isFinite(data.totalElements) ? data.totalElements : content.length;
  const size = Number.isFinite(data.size) ? data.size : content.length || 25;
  const page = Number.isFinite(data.number) ? data.number : 0;
  return { items: content, totalPages, page, size, totalElements };
};

const formatDateTime = (value, locale = "nl-NL") => {
  if (!value) return "";
  const d = parseToDate(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(locale, {
    dateStyle: "medium",
    timeStyle: "medium",
  });
};

const stringifyMeta = (meta) => {
  if (!meta) return "";
  if (typeof meta === "string") return meta;
  try {
    return JSON.stringify(meta, null, 2);
  } catch (e) {
    return "";
  }
};

const levelClasses = (level) => {
  const lvl = String(level || "").toLowerCase();
  if (["error", "err", "fatal"].includes(lvl)) {
    return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200";
  }
  if (["warn", "warning"].includes(lvl)) {
    return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-100";
  }
  if (["info", "information"].includes(lvl)) {
    return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-100";
  }
  if (["debug", "trace"].includes(lvl)) {
    return "bg-slate-100 text-slate-800 dark:bg-slate-900/40 dark:text-slate-100";
  }
  if (lvl) {
    return "bg-gray-100 text-gray-800 dark:bg-gray-800/40 dark:text-gray-100";
  }
  return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-100";
};

const getInitials = (name, fallback = "SY") => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return fallback;
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const humanizeAction = (action) => {
  const raw = String(action || "").trim();
  if (!raw) return "heeft een gebeurtenis uitgevoerd";
  return `heeft ${raw.toLowerCase().replace(/_/g, " ")}`;
};

const formatTimeOnly = (value, locale = "nl-NL") => {
  if (!value) return "";
  const d = parseToDate(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

const formatDayHeading = (value, locale = "nl-NL") => {
  const d = parseToDate(value);
  if (Number.isNaN(d.getTime())) return "Onbekend";
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const formatted = d.toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
  if (sameDay(d, today)) return `Vandaag - ${formatted}`;
  if (sameDay(d, yesterday)) return `Gisteren - ${formatted}`;
  return formatted;
};

const groupLogsByDay = (items, locale) => {
  const groups = [];
  items.forEach((entry) => {
    const when = entry?.dateTime || entry?.timestamp || entry?.time || null;
    const label = formatDayHeading(when, locale);
    const last = groups[groups.length - 1];
    if (last?.label === label) {
      last.items.push(entry);
    } else {
      groups.push({ label, items: [entry] });
    }
  });
  return groups;
};

const fieldIconFor = (field) => {
  const normalized = String(field || "").toLowerCase();
  if (normalized.includes("mail")) return FaEnvelope;
  if (normalized.includes("phone") || normalized.includes("telefoon")) return FaPhone;
  if (normalized.includes("address") || normalized.includes("adres")) return FaHome;
  return FaClipboardList;
};

const entityTypeLabel = (type) => {
  const labels = { FAMILY: "Family", CHILD: "Child", CLASSROOM: "Classroom" };
  return labels[String(type || "").toUpperCase()] || String(type || "Entity");
};

const functionalEntityLabel = (type) => {
  const labels = { FAMILY: "familie", CHILD: "kind", CLASSROOM: "klas" };
  return labels[String(type || "").toUpperCase()] || "entiteit";
};

export const buildDestructiveAuditPresentation = (entry, changes = parseFieldChanges(entry?.fieldChanges)) => {
  const primaryEntity = entry?.primaryEntity || changes?.primaryEntity || null;
  if (!primaryEntity) return null;
  const actorName = entry?.displayName || entry?.email || entry?.userId || "Onbekende gebruiker";
  const entityLabel = functionalEntityLabel(primaryEntity.type);
  const failed = String(entry?.action || "").toUpperCase().includes("FAILED")
    || (Number.isFinite(Number(entry?.status)) && Number(entry.status) >= 400);
  const actionLabel = failed
    ? `${entityLabel.charAt(0).toUpperCase()}${entityLabel.slice(1)} verwijderen mislukt`
    : `${entityLabel.charAt(0).toUpperCase()}${entityLabel.slice(1)} verwijderd`;
  const entityName = primaryEntity.displayName || primaryEntity.id || entityLabel;
  return {
    actorName,
    entityLabel,
    entityName,
    actionLabel,
    failed,
    headline: failed
      ? `${actorName} kon ${entityLabel} "${entityName}" niet verwijderen`
      : `${actorName} heeft ${entityLabel} "${entityName}" verwijderd`,
  };
};

export const collapseDestructiveAuditEntries = (entries = []) => {
  const knownEntities = entries
    .map((entry) => entry?.primaryEntity || parseFieldChanges(entry?.fieldChanges)?.primaryEntity)
    .filter((entity) => entity?.id);
  const groups = new Map();
  const output = [];

  entries.forEach((original) => {
    const parsed = parseFieldChanges(original?.fieldChanges);
    const primaryEntity = original?.primaryEntity || parsed?.primaryEntity
      || knownEntities.find((entity) => String(original?.path || "").includes(String(entity.id)));
    const isDelete = primaryEntity && `${original?.action || ""} ${original?.method || ""}`.toUpperCase().includes("DELETE");
    if (!isDelete) {
      output.push(original);
      return;
    }
    const failed = String(original?.action || "").toUpperCase().includes("FAILED") || Number(original?.status) >= 400;
    const groupKey = `${primaryEntity.type || "ENTITY"}:${primaryEntity.id}:${failed ? "FAILED" : "SUCCESS"}`;
    const current = groups.get(groupKey);
    if (!current) {
      const item = { ...original, primaryEntity };
      groups.set(groupKey, { item, outputIndex: output.length });
      output.push(item);
      return;
    }
    const existing = current.item;
    const identity = existing.displayName || existing.email ? existing : original;
    const directPath = [existing, original].find((item) =>
      String(item?.path || "").match(/^\/api\/v1\/(children|families|classrooms)\/[^/]+$/)
    );
    const auditAction = [existing, original].find((item) => String(item?.action || "").toUpperCase() === "DELETE_REQUESTED");
    const merged = {
      ...existing,
      displayName: identity.displayName || existing.displayName || original.displayName,
      email: identity.email || existing.email || original.email,
      userId: identity.userId || existing.userId || original.userId,
      action: auditAction?.action || existing.action || original.action,
      method: directPath?.method || existing.method || original.method,
      path: directPath?.path || existing.path || original.path,
      status: directPath?.status ?? existing.status ?? original.status,
      ip: directPath?.ip || existing.ip || original.ip,
      primaryEntity,
      affectedEntities: existing.affectedEntities?.length ? existing.affectedEntities : original.affectedEntities,
    };
    current.item = merged;
    output[current.outputIndex] = merged;
  });
  return output;
};

export const buildAuditQueryParams = (filters, page = 0, size = 50, sortMode = "date_desc") => {
  const sortParams = {
    date_asc: "dateTime,asc",
    date_desc: "dateTime,desc",
    action_asc: "action,asc",
    action_desc: "action,desc",
    actor_asc: "displayName,asc",
    actor_desc: "displayName,desc",
  };
  const params: any = { page, size: Math.min(Math.max(size, 1), 50), sort: sortParams[sortMode] || "dateTime,desc" };
  if (filters.search) params.search = filters.search;
  if (filters.action) params.action = filters.action;
  if (filters.userId) params.user = filters.userId;
  if (filters.path) params.path = filters.path;
  if (filters.status) params.status = filters.status;
  if (filters.ip) params.ip = filters.ip;
  return params;
};

const AdminLogs = () => {
  const { t, i18n } = useTranslation();
  const canDeleteAuditLogs = useCan("admin.audit", "delete");
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [sortMode, setSortMode] = useState("date_desc");
  const [compactFilters, setCompactFilters] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [filtersPinned, setFiltersPinned] = useState(false);
  const [hoverCapable, setHoverCapable] = useState(false);
  const [expandedTextKeys, setExpandedTextKeys] = useState<Set<string>>(() => new Set());
  const [expandedLogKeys, setExpandedLogKeys] = useState<Set<string>>(() => new Set());
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState({ show: false, entry: null });
  const [deleting, setDeleting] = useState(false);
  const [filters, setFilters] = useState({
    action: "",
    search: "",
    userId: "",
    path: "",
    status: "",
    ip: "",
    from: "",
    to: "",
  });
  const [pageMeta, setPageMeta] = useState({ totalPages: 1, page: 0, size: 50, totalElements: 0 });
  const [lastUpdated, setLastUpdated] = useState(null);
  const lastQueryRef = useRef({ action: "", search: "", userId: "", path: "", status: "", ip: "", from: "", to: "" });

  const toIsoOrNull = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  };

  const getLogKey = (entry, idx = 0) =>
    entry?.id ||
    entry?._id ||
    entry?.uuid ||
    entry?.logId ||
    entry?.auditId ||
    entry?.eventId ||
    entry?.dateTime ||
    entry?.timestamp ||
    entry?.time ||
    `log-${idx}`;

  const getLogDeleteId = (entry) =>
    entry?.id ||
    entry?._id ||
    entry?.uuid ||
    entry?.logId ||
    entry?.auditId ||
    entry?.eventId ||
    null;

  const toggleExpandedText = (key) => {
    setExpandedTextKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleLogExpanded = (key) => {
    setExpandedLogKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const copyText = (value) => {
    const text = value == null ? "" : String(value);
    if (!text || typeof navigator === "undefined" || !navigator.clipboard) return;
    navigator.clipboard.writeText(text).catch(() => {});
  };

  const ExpandableText = ({ text, idKey, maxChars = 260, className = "" }) => {
    const str = text == null ? "" : String(text);
    const isLong = str.length > maxChars;
    const expanded = expandedTextKeys.has(idKey);
    const shown = !isLong || expanded ? str : `${str.slice(0, maxChars)}…`;
    return (
      <span className={`max-w-full ${className}`}>
        <span className="whitespace-pre-wrap break-words break-all">{shown}</span>
        {isLong && (
          <button
            type="button"
            onClick={() => toggleExpandedText(idKey)}
            className="ml-2 inline-flex items-center rounded-full bg-white/60 px-2 py-0.5 text-[10px] font-extrabold text-indigo-700 hover:bg-white dark:bg-gray-900/40 dark:text-indigo-200"
          >
            {expanded
              ? t("common.less", { defaultValue: "Minder" })
              : t("common.more", { defaultValue: "Meer" })}
          </button>
        )}
      </span>
    );
  };

  const performDeleteLogEntry = async (entry) => {
    const id = getLogDeleteId(entry);
    if (!id) return;

    try {
      setDeleting(true);
      const candidates = [`/audit/${encodeURIComponent(id)}`, `/audit/logs/${encodeURIComponent(id)}`];
      let lastErr = null;
      for (const url of candidates) {
        try {
          await http.delete(url);
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
        }
      }
      if (lastErr) throw lastErr;

      setLogs((prev) => (prev || []).filter((it) => getLogKey(it) !== getLogKey(entry)));
      setPageMeta((prev) => ({
        ...prev,
        totalElements: Math.max(0, (prev?.totalElements || 0) - 1),
      }));
    } catch (e) {
      setError(mapHttpErrorToMessage(e, t) || e);
    } finally {
      setDeleting(false);
    }
  };

  const fetchLogs = async (opts: any = {}) => {
    setError(null);
    setRefreshing(true);
    try {
      const activeSortMode = opts.sortMode ?? sortMode;
      const currentFilters = {
        action: opts.action ?? filters.action,
        search: opts.search ?? filters.search,
        userId: opts.userId ?? filters.userId,
        path: opts.path ?? filters.path,
        status: opts.status ?? filters.status,
        ip: opts.ip ?? filters.ip,
        from: opts.from ?? filters.from,
        to: opts.to ?? filters.to,
      };
      const uiPage = Number.isFinite(opts.page) ? opts.page : pageMeta.page;
      const uiSize = Number.isFinite(opts.size) ? opts.size : pageMeta.size;
      const params: any = buildAuditQueryParams(currentFilters, uiPage, uiSize, activeSortMode);
      if (currentFilters.from) {
        const iso = toIsoOrNull(currentFilters.from);
        if (iso) params.from = iso;
      }
      if (currentFilters.to) {
        const iso = toIsoOrNull(currentFilters.to);
        if (iso) params.to = iso;
      }
      const { data } = await http.get("/audit", { params });
      const normalized = normalizePage(data);
      const presentedItems = collapseDestructiveAuditEntries(normalized.items);
      setLogs(presentedItems);
      setPageMeta({
        totalPages: normalized.totalPages || 1,
        page: uiPage || 0,
        size: normalized.size || uiSize || 50,
        totalElements: Math.max(0, (normalized.totalElements || normalized.items.length) - (normalized.items.length - presentedItems.length)),
      });
      lastQueryRef.current = currentFilters;
      setLastUpdated(new Date());
    } catch (e) {
      setError(mapHttpErrorToMessage(e, t) || e);
      setLogs([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLogs({ page: 0, sortMode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortMode]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    setHoverCapable(window.matchMedia("(hover: hover) and (pointer: fine)").matches);
    return undefined;
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onScroll = () => setCompactFilters(window.scrollY > 120);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setFiltersPinned(false);
    setFiltersExpanded(!compactFilters);
  }, [compactFilters]);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchLogs({ page: 0 });
  };

  const setFilterField = (key, value) =>
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));

  const lastQuery = lastQueryRef.current || {};
  const totalLabel = t("common.items", { defaultValue: "items" });
  const hasFilters =
    Boolean(filters.action) ||
    Boolean(filters.search) ||
    Boolean(filters.userId) ||
    Boolean(filters.path) ||
    Boolean(filters.status) ||
    Boolean(filters.ip) ||
    Boolean(filters.from) ||
    Boolean(filters.to);
  const activeAdvancedFilterCount = ["action", "userId", "path", "status", "ip", "from", "to"].filter(
    (key) => Boolean(filters[key])
  ).length;

  const userSuggestions = useMemo(() => {
    const entries = new Map();
    logs.forEach((l) => {
      const id =
        l?.userId ||
        l?.user_id ||
        l?.actorId ||
        (l?.actor && typeof l.actor === "object" ? l.actor.id : null);
      if (typeof id !== "string" || !looksLikeUuid(id)) return;
      const nameFromLog =
        l?.displayName || l?.email ||
        l?.userName ||
        l?.username ||
        l?.actorName ||
        (l?.actor && typeof l.actor === "object" ? l.actor.name : null);
      const name = nameFromLog || null;
      if (!entries.has(id)) {
        entries.set(id, name);
      }
    });
    return Array.from(entries.entries())
      .slice(0, 20)
      .map(([id, name]) => ({ id, name }));
  }, [logs]);

  const actionSuggestions = useMemo(() => {
    const values = logs
      .map((l) => l?.action)
      .filter((a) => typeof a === "string" && a.trim().length > 0)
      .map((a) => a.trim());
    return Array.from(new Set(values)).slice(0, 20);
  }, [logs]);

  const visibleLogs = logs;

  const hasMore = (pageMeta.page + 1) < (pageMeta.totalPages || 1);

  const groupedVisibleLogs = useMemo(
    () => groupLogsByDay(visibleLogs, i18n.language || "nl-NL"),
    [visibleLogs, i18n.language]
  );

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaClipboardList}
        eyebrow={t("admin.logs.eyebrow", { defaultValue: "Audit" })}
        title={t("admin.logs.title", { defaultValue: "Logging" })}
        description={t("admin.logs.description", {
          defaultValue: "Bekijk recente auditregels en wijzigingen in het systeem.",
        })}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center rounded-full bg-white/80 px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm ring-1 ring-indigo-100 dark:bg-indigo-950/30 dark:text-indigo-100 dark:ring-indigo-500/30">
              {visibleLogs.length} / {pageMeta.totalElements} {totalLabel}
            </span>
            {lastUpdated && (
              <span className="inline-flex items-center rounded-full bg-white/80 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm ring-1 ring-slate-200/70 dark:bg-slate-950/30 dark:text-slate-200 dark:ring-white/10">
                {formatDateTime(lastUpdated, i18n.language)}
              </span>
            )}
          </div>
        }
      />
      <div className="space-y-4">
        <form
          onSubmit={handleSubmit}
          className="sticky top-4 z-30 rounded-[24px] border border-slate-200/80 bg-white/95 p-5 shadow-sm backdrop-blur-sm dark:border-white/10 dark:bg-slate-900/80"
          onMouseEnter={
            compactFilters && hoverCapable && !filtersPinned
              ? () => setFiltersExpanded(true)
              : undefined
          }
          onMouseLeave={
            compactFilters && hoverCapable && !filtersPinned
              ? () => setFiltersExpanded(false)
              : undefined
          }
        >
          <div className="grid items-end gap-4 lg:grid-cols-[minmax(280px,1fr)_auto_auto_auto_auto]">
            <div className="flex min-w-[240px] flex-col gap-1">
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300">
                {t("admin.logs.value_search", { defaultValue: "Zoek in waarden" })}
              </label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilterField("search", e.target.value)}
                placeholder={t("admin.logs.value_placeholder", {
                  defaultValue: "Zoek op naam, bsn, record-id, etc.",
                })}
                className="w-full rounded-xl border border-indigo-100 bg-white/80 px-3 py-2 text-sm text-gray-800 shadow-inner transition focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-indigo-500/40 dark:bg-gray-900/70 dark:text-gray-100"
              />
            </div>
            <label className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                {t("common.sort", { defaultValue: "Sorteren" })}
              </span>
              <select
                value={sortMode}
                onChange={(e) => {
                  const next = e.target.value;
                  setSortMode(next);
                }}
                className="rounded-full border border-slate-200/80 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:text-slate-900 dark:border-white/10 dark:bg-slate-950/30 dark:text-slate-100"
              >
                <option value="date_desc">
                  {t("admin.logs.sort_date_desc", { defaultValue: "Datum (nieuw → oud)" })}
                </option>
                <option value="date_asc">
                  {t("admin.logs.sort_date_asc", { defaultValue: "Datum (oud → nieuw)" })}
                </option>
                <option value="action_asc">
                  {t("admin.logs.sort_action_asc", { defaultValue: "Actie (A → Z)" })}
                </option>
                <option value="action_desc">
                  {t("admin.logs.sort_action_desc", { defaultValue: "Actie (Z → A)" })}
                </option>
                <option value="actor_asc">
                  {t("admin.logs.sort_actor_asc", { defaultValue: "Gebruiker (A → Z)" })}
                </option>
                <option value="actor_desc">
                  {t("admin.logs.sort_actor_desc", { defaultValue: "Gebruiker (Z → A)" })}
                </option>
              </select>
            </label>
            {compactFilters && (
              <button
                type="button"
                onClick={() => {
                  setFiltersPinned((prevPinned) => {
                    const nextPinned = !prevPinned;
                    setFiltersExpanded(nextPinned);
                    return nextPinned;
                  });
                }}
                className="inline-flex items-center gap-2 rounded-full border border-indigo-100/60 bg-white/70 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:text-gray-900 dark:border-indigo-500/40 dark:bg-gray-900/50 dark:text-gray-100"
              >
                {filtersExpanded
                  ? t("common.less", { defaultValue: "Minder" })
                  : t("common.filters", { defaultValue: "Filters" })}
                {activeAdvancedFilterCount > 0 && (
                  <span className="inline-flex min-w-6 items-center justify-center rounded-full bg-indigo-600 px-2 py-0.5 text-xs font-bold text-white">
                    {activeAdvancedFilterCount}
                  </span>
                )}
              </button>
            )}
            <button
              type="submit"
              className="flex items-center gap-2 rounded-full border border-indigo-100/60 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:text-indigo-900 dark:border-indigo-500/40 dark:bg-indigo-900/30 dark:text-indigo-100"
            >
              <FaSearch />
              {t("common.actions.search", { defaultValue: "Zoeken" })}
            </button>
            <button
              type="button"
              onClick={() => fetchLogs({ page: 0 })}
              disabled={refreshing}
              className="flex items-center gap-2 rounded-full border border-slate-200/80 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:text-slate-900 disabled:opacity-60 dark:border-white/10 dark:bg-slate-950/30 dark:text-slate-100"
            >
              <FaSyncAlt className={refreshing ? "animate-spin" : ""} />
              {refreshing
                ? t("common.loading", { defaultValue: "Laden…" })
                : t("admin.logs.refresh", { defaultValue: "Vernieuwen" })}
            </button>
            <button
              type="button"
              onClick={() => {
                setFilters({ action: "", search: "", userId: "", path: "", status: "", ip: "", from: "", to: "" });
                fetchLogs({ action: "", search: "", userId: "", path: "", status: "", ip: "", from: "", to: "", page: 0 });
              }}
              disabled={!hasFilters}
              className="rounded-full border border-slate-200/80 bg-white px-4 py-2 text-sm font-semibold text-slate-500 shadow-sm transition hover:text-slate-900 disabled:opacity-50 dark:border-white/10 dark:bg-slate-950/30 dark:text-slate-100"
            >
              {t("common.reset", { defaultValue: "Reset" })}
            </button>
          </div>
          {(!compactFilters || filtersExpanded) && (
            <div className="mt-4 grid items-end gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="flex min-w-[200px] flex-col gap-1">
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {t("admin.logs.action_label", { defaultValue: "Actie" })}
                </label>
                <div className="relative">
                  <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={filters.action}
                    onChange={(e) => setFilterField("action", e.target.value)}
                    placeholder={t("admin.logs.action_placeholder", {
                      defaultValue: "Actie (bijv. ALLOW, DENY)",
                    })}
                    list="admin-log-action-suggestions"
                    className="w-full rounded-xl border border-indigo-100 bg-white/80 py-2 pl-10 pr-3 text-sm text-gray-800 shadow-inner transition focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-indigo-500/40 dark:bg-gray-900/70 dark:text-gray-100"
                  />
                  {actionSuggestions.length > 0 && (
                    <datalist id="admin-log-action-suggestions">
                      {actionSuggestions.map((a) => (
                        <option key={a} value={a} />
                      ))}
                    </datalist>
                  )}
                </div>
              </div>

              <div className="flex min-w-[200px] flex-col gap-1">
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {t("admin.logs.user", { defaultValue: "User / name / email" })}
                </label>
                <input
                  type="text"
                  value={filters.userId}
                  onChange={(e) => setFilterField("userId", e.target.value)}
                  placeholder="Name, email or user UUID"
                  list="admin-log-user-suggestions"
                  className="w-full rounded-xl border border-indigo-100 bg-white/80 px-3 py-2 text-sm text-gray-800 shadow-inner transition focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-indigo-500/40 dark:bg-gray-900/70 dark:text-gray-100"
                />
                {userSuggestions.length > 0 && (
                  <datalist id="admin-log-user-suggestions">
                    {userSuggestions.map(({ id, name }) => (
                      <option key={id} value={id}>
                        {name || id}
                      </option>
                    ))}
                  </datalist>
                )}
              </div>

              <div className="flex min-w-[200px] flex-col gap-1">
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300">Request path</label>
                <input type="text" value={filters.path} onChange={(e) => setFilterField("path", e.target.value)}
                  placeholder="/api/v1/families/…"
                  className="w-full rounded-xl border border-indigo-100 bg-white/80 px-3 py-2 text-sm text-gray-800 shadow-inner transition focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-indigo-500/40 dark:bg-gray-900/70 dark:text-gray-100" />
              </div>

              <div className="flex min-w-[160px] flex-col gap-1">
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300">HTTP status</label>
                <input type="number" value={filters.status} onChange={(e) => setFilterField("status", e.target.value)}
                  placeholder="204"
                  className="w-full rounded-xl border border-indigo-100 bg-white/80 px-3 py-2 text-sm text-gray-800 shadow-inner transition focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-indigo-500/40 dark:bg-gray-900/70 dark:text-gray-100" />
              </div>

              <div className="flex min-w-[180px] flex-col gap-1">
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300">IP address</label>
                <input type="text" value={filters.ip} onChange={(e) => setFilterField("ip", e.target.value)}
                  placeholder="192.0.2.1"
                  className="w-full rounded-xl border border-indigo-100 bg-white/80 px-3 py-2 text-sm text-gray-800 shadow-inner transition focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-indigo-500/40 dark:bg-gray-900/70 dark:text-gray-100" />
              </div>

              <div className="flex min-w-[220px] flex-col gap-1">
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {t("admin.logs.from", { defaultValue: "Vanaf" })}
                </label>
                <input
                  type="datetime-local"
                  value={filters.from}
                  onChange={(e) => setFilterField("from", e.target.value)}
                  className="w-full rounded-xl border border-indigo-100 bg-white/80 px-3 py-2 text-sm text-gray-800 shadow-inner transition focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-indigo-500/40 dark:bg-gray-900/70 dark:text-gray-100"
                />
              </div>

              <div className="flex min-w-[220px] flex-col gap-1">
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {t("admin.logs.to", { defaultValue: "Tot" })}
                </label>
                <input
                  type="datetime-local"
                  value={filters.to}
                  onChange={(e) => setFilterField("to", e.target.value)}
                  className="w-full rounded-xl border border-indigo-100 bg-white/80 px-3 py-2 text-sm text-gray-800 shadow-inner transition focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-indigo-500/40 dark:bg-gray-900/70 dark:text-gray-100"
                />
              </div>
            </div>
          )}

          <div className="mt-3 text-right text-xs text-gray-500 dark:text-gray-400">
            <div>
              {visibleLogs.length} / {pageMeta.totalElements} {totalLabel}
            </div>
            {(lastQuery.action || lastQuery.userId || lastQuery.search) && (
              <div>
                {t("admin.logs.server_filter", { defaultValue: "Serverfilter" })}:{" "}
                {[lastQuery.action, lastQuery.userId, lastQuery.search].filter(Boolean).join(" • ")}
              </div>
            )}
            {lastUpdated && (
              <div>
                {t("admin.logs.updated", { defaultValue: "Bijgewerkt" })}:{" "}
                {formatDateTime(lastUpdated, i18n.language)}
              </div>
            )}
          </div>
        </form>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50/70 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/40 dark:text-red-100">
            {error?.message || error?.title || String(error)}
          </div>
        )}

        {loading ? (
          <Spinner />
        ) : (
          <div className="rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
	            <div className="mb-5 flex items-center justify-between">
	              <div>
	                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
	                  {t("admin.logs.latest", { defaultValue: "Recente logging" })}
	                </h2>
	                <p className="text-sm text-gray-500 dark:text-gray-400">
	                  {t("admin.logs.description")}
	                </p>
	              </div>
	              <div className="text-xs text-gray-500 dark:text-gray-400">
	                {t("admin.logs.infinite_hint", { defaultValue: "Scroll om meer te laden" })}
	              </div>
	            </div>

            {visibleLogs.length === 0 ? (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t("admin.logs.empty", { defaultValue: "Geen logs gevonden voor deze filter." })}{" "}
                {t("admin.logs.empty_hint", {
                  defaultValue: "Controleer je rechten (admin.audit:read) of pas je filters/datum aan.",
                })}
              </p>
            ) : (
              <div className="space-y-5">
                {groupedVisibleLogs.map((group) => (
                  <section key={group.label} className="space-y-3">
                    <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">{group.label}</h3>
                    <div className="space-y-3">
                      {group.items.map((entry, idx) => {
                        const key = String(getLogKey(entry, idx));
                        const when = entry?.dateTime || entry?.timestamp || entry?.time || null;
                  const actorId =
                    entry?.userId ||
                    entry?.user_id ||
                    entry?.actorId ||
                    (entry?.actor && typeof entry.actor === "object" ? entry.actor.id : "") ||
                    "";
                  const actorName =
                    entry?.displayName ||
                    entry?.userName ||
                    entry?.username ||
                    entry?.actorName ||
                    entry?.email ||
                    (entry?.actor && typeof entry.actor === "object" ? entry.actor.name : null) ||
                    "";
                  const action = entry?.action || "";
                  const level = entry?.status || entry?.severity || entry?.level || "";
                  const source = entry?.tableName || "";
                        const parsedChanges = parseFieldChanges(entry?.fieldChanges);
                        const primaryEntity = entry?.primaryEntity || parsedChanges?.primaryEntity || null;
                        const affectedEntities = Array.isArray(entry?.affectedEntities)
                          ? entry.affectedEntities
                          : Array.isArray(parsedChanges?.affectedEntities)
                          ? parsedChanges.affectedEntities
                          : [];
                        const requestDetails = parsedChanges?.request || null;
                        const isDestructiveAudit = Boolean(primaryEntity);
                        const destructivePresentation = buildDestructiveAuditPresentation(entry, parsedChanges);
                        const diffRows = isDestructiveAudit ? [] : buildChangeRows(parsedChanges);
                        const metaText = stringifyMeta(entry?.fieldChanges);
                        const deleteId = getLogDeleteId(entry);
                  const recordId =
                    entry?.recordId ||
                    entry?.rowId ||
                    entry?.entityId ||
                    entry?.entity_id ||
                    entry?.pk ||
                    entry?.primaryKey ||
                    (parsedChanges && typeof parsedChanges === "object" && !Array.isArray(parsedChanges)
                      ? parsedChanges.id || parsedChanges.uuid || parsedChanges._id
                      : null);
                        const isSensitiveBsnEvent = (() => {
                          const actionStr = String(action || "").toUpperCase();
                          if (actionStr === "VIEW_BSN" || actionStr.includes("BSN")) return true;
                          const hay = String(metaText || "").toLowerCase();
                          if (hay.includes("children.bsn")) return true;
                          if (diffRows.some((row) => String(row?.field || "").toUpperCase() === "RESOURCE" && String(row?.next || row?.prev || "").toLowerCase().includes("children.bsn"))) {
                            return true;
                          }
                          if (diffRows.some((row) => String(row?.field || "").toLowerCase().includes("bsn"))) return true;
                          return false;
                        })();
                        const isSensitiveBankEvent = (() => {
                          const haystack = [
                            action,
                            source,
                            metaText,
                            ...diffRows.flatMap((row) => [
                              row?.field,
                              normalizeChangeValue(row?.prev),
                              normalizeChangeValue(row?.next),
                            ]),
                          ]
                            .filter(Boolean)
                            .join(" ")
                            .toLowerCase();
                          return (
                            haystack.includes("bank") ||
                            haystack.includes("iban") ||
                            haystack.includes("rekening") ||
                            haystack.includes("account_lookup") ||
                            haystack.includes("financial")
                          );
                        })();
                  const changeCount =
                    diffRows.length ||
                    (parsedChanges && typeof parsedChanges === "object" && !Array.isArray(parsedChanges)
                      ? Object.keys(parsedChanges).length
                      : 0);
                        const actorLabel = actorName || actorId || t("common.system", { defaultValue: "Systeem" });
                        const roleLabel = String(actorLabel).toLowerCase().includes("systeem")
                          ? t("common.system", { defaultValue: "System" })
                          : t("admin.roles.admin", { defaultValue: "Admin" });
                        const timeLabel = formatTimeOnly(when, i18n.language) || t("common.unknown", { defaultValue: "Onbekend" });
                        const isExpanded = expandedLogKeys.has(key);
                        const usageDetails = [entry?.method, entry?.path, entry?.status, entry?.ip].filter((value) => value !== null && value !== undefined && value !== "").join(" • ");
                        const hasDetails = diffRows.length > 0 || Boolean(metaText) || Boolean(usageDetails) || isDestructiveAudit;
                        const isMenuOpen = openMenuKey === key;

                        return (
                          <article
                            key={key}
                            className={`rounded-[18px] border bg-white px-4 py-4 shadow-sm transition hover:shadow-md dark:bg-slate-950/20 ${
                              isSensitiveBsnEvent
                                ? "border-rose-200 bg-gradient-to-r from-rose-50/90 via-white to-white shadow-rose-100/70 dark:border-rose-500/40 dark:from-rose-950/25 dark:via-slate-950/20 dark:to-slate-950/20"
                                : isSensitiveBankEvent
                                  ? "border-amber-200 bg-gradient-to-r from-amber-50/80 via-white to-white shadow-amber-100/60 dark:border-amber-500/40 dark:from-amber-950/20 dark:via-slate-950/20 dark:to-slate-950/20"
                                : "border-slate-200/80 dark:border-white/10"
                            }`}
                          >
                            <div className="grid gap-4 lg:grid-cols-[72px_190px_minmax(0,1fr)_250px] lg:items-start">
                              <div className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                                {timeLabel}
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-200 to-indigo-50 text-sm font-bold text-indigo-700 shadow-sm ring-1 ring-indigo-100 dark:from-violet-900 dark:to-indigo-950 dark:text-indigo-100 dark:ring-white/10">
                                  {getInitials(actorLabel)}
                                </div>
                                <div className="min-w-0">
                                  <div className="truncate text-sm font-bold text-slate-950 dark:text-white">
                                    {actorLabel}
                                  </div>
                                  {entry?.email && entry.email !== actorName ? (
                                    <div className="truncate text-xs text-slate-500 dark:text-slate-400">{entry.email}</div>
                                  ) : null}
                                </div>
                              </div>

                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2 text-base font-semibold text-slate-950 dark:text-white">
                                  {isSensitiveBsnEvent ? <FaLock className="text-rose-500" aria-hidden="true" /> : null}
                                  {!isSensitiveBsnEvent && isSensitiveBankEvent ? <FaCreditCard className="text-amber-500" aria-hidden="true" /> : null}
                                  <span>
                                    {destructivePresentation?.headline || humanizeAction(action)}
                                  </span>
                                  {isSensitiveBsnEvent ? (
                                    <span className="inline-flex items-center rounded-full bg-rose-100 px-3 py-1 text-[11px] font-bold text-rose-700 dark:bg-rose-900/40 dark:text-rose-100">
                                      {t("admin.logs.sensitive_bsn", { defaultValue: "Sensitive data" })}
                                    </span>
                                  ) : null}
                                  {!isSensitiveBsnEvent && isSensitiveBankEvent ? (
                                    <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-[11px] font-bold text-amber-800 dark:bg-amber-900/40 dark:text-amber-100">
                                      {t("admin.logs.sensitive_bank", { defaultValue: "Bankgegevens" })}
                                    </span>
                                  ) : null}
                                </div>
                                <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                  {isDestructiveAudit
                                    ? destructivePresentation?.actionLabel
                                    : changeCount > 0
                                    ? `${changeCount} ${t("admin.logs.fields_changed", { defaultValue: "velden gewijzigd" })}`
                                    : metaText
                                      ? t("admin.logs.technical_details_available", { defaultValue: "Technische details beschikbaar." })
                                      : t("admin.logs.no_change_details", { defaultValue: "Geen wijzigingsdetails." })}
                                </div>
                                <div className="mt-3 flex flex-wrap items-center gap-2">
                                  {!isDestructiveAudit && source ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-100">
                                      <FaFolder aria-hidden="true" />
                                      {source}
                                    </span>
                                  ) : null}
                                  {!isDestructiveAudit && action ? (
                                    <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-100">
                                      {String(action).toUpperCase()}
                                    </span>
                                  ) : null}
                                  {!isDestructiveAudit && entry?.path ? (
                                    <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-100">
                                      {entry.method} {entry.path}
                                    </span>
                                  ) : null}
                                  {!isDestructiveAudit && entry?.ip ? (
                                    <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-100">IP {entry.ip}</span>
                                  ) : null}
                                  {!isDestructiveAudit && changeCount > 0 ? (
                                    <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-100">
                                      {changeCount} {changeCount === 1 ? "change" : "changes"}
                                    </span>
                                  ) : null}
                                </div>
                              </div>

                              <div className="relative flex items-start justify-end gap-3">
                                <span className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold ${destructivePresentation?.failed ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200" : level && !isDestructiveAudit ? levelClasses(level) : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-100"}`}>
                                  {isDestructiveAudit
                                    ? destructivePresentation?.failed ? "MISLUKT" : "GESLAAGD"
                                    : level ? String(level).toUpperCase() : "SUCCESS"}
                                </span>
                                <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-3 py-1 text-[11px] font-bold text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-100">
                                  <FaShieldAlt aria-hidden="true" />
                                  {roleLabel}
                                </span>
                                {hasDetails ? (
                                  <button
                                    type="button"
                                    onClick={() => toggleLogExpanded(key)}
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-indigo-700 dark:text-slate-300 dark:hover:bg-slate-800"
                                    aria-label={
                                      isExpanded
                                        ? t("admin.logs.hide_changes", { defaultValue: "Wijzigingen verbergen" })
                                        : t("admin.logs.show_changes", { defaultValue: "Wijzigingen tonen" })
                                    }
                                  >
                                    {isExpanded ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}
                                  </button>
                                ) : null}
                                {canDeleteAuditLogs && entry?.sourceType !== "usage_event" ? (
                                  <button
                                    type="button"
                                    onClick={() => setOpenMenuKey((current) => (current === key ? null : key))}
                                    disabled={!deleteId}
                                    title={
                                      deleteId
                                        ? t("common.actions.name", { defaultValue: "Acties" })
                                        : t("admin.logs.delete_unavailable", { defaultValue: "Deze logregel heeft geen id en kan niet verwijderd worden." })
                                    }
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-800"
                                  >
                                    <FaEllipsisV aria-hidden="true" />
                                  </button>
                                ) : null}
                                {isMenuOpen ? (
                                  <div className="absolute right-0 top-10 z-20 w-64 rounded-[14px] border border-slate-200 bg-white p-2 text-sm shadow-xl dark:border-white/10 dark:bg-slate-950">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        toggleLogExpanded(key);
                                        setOpenMenuKey(null);
                                      }}
                                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-900"
                                    >
                                      <FaShieldAlt aria-hidden="true" />
                                      {isExpanded
                                        ? t("admin.logs.hide_changes", { defaultValue: "Wijzigingen verbergen" })
                                        : t("admin.logs.show_changes", { defaultValue: "Technische details bekijken" })}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        copyText(deleteId || key);
                                        setOpenMenuKey(null);
                                      }}
                                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-900"
                                    >
                                      <FaCopy aria-hidden="true" />
                                      {t("admin.logs.copy_audit_id", { defaultValue: "Audit ID kopiëren" })}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        copyText(recordId);
                                        setOpenMenuKey(null);
                                      }}
                                      disabled={!recordId}
                                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:text-slate-200 dark:hover:bg-slate-900"
                                    >
                                      <FaCopy aria-hidden="true" />
                                      {t("admin.logs.copy_record_id", { defaultValue: "Record ID kopiëren" })}
                                    </button>
                                    <div className="my-2 border-t border-slate-200 dark:border-white/10" />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenMenuKey(null);
                                        setConfirmDelete({ show: true, entry });
                                      }}
                                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-rose-600 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-950/30"
                                    >
                                      <FaTrash aria-hidden="true" />
                                      {t("admin.logs.delete_entry", { defaultValue: "Log entry verwijderen" })}
                                    </button>
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            {isExpanded && hasDetails ? (
                              <div className="mt-3 flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => toggleLogExpanded(key)}
                                  className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 text-xs font-bold text-slate-700 shadow-sm transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                                >
                                  {t("admin.logs.hide_changes", { defaultValue: "Wijzigingen verbergen" })}
                                  <FaChevronUp aria-hidden="true" />
                                </button>
                              </div>
                            ) : null}

                            {isExpanded && isDestructiveAudit ? (
                              <div className="ml-0 mt-4 space-y-4 rounded-[14px] border border-slate-200/80 bg-white p-4 text-sm dark:border-white/10 dark:bg-slate-950/30 lg:ml-[262px]">
                                <div>
                                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
                                    {destructivePresentation
                                      ? `${destructivePresentation.entityLabel.charAt(0).toUpperCase()}${destructivePresentation.entityLabel.slice(1)}`
                                      : entityTypeLabel(primaryEntity.type)}
                                  </div>
                                  <dl className="mt-2 grid gap-2 sm:grid-cols-[140px_1fr]">
                                    <dt className="text-slate-500">Naam</dt>
                                    <dd className="font-semibold text-slate-900 dark:text-slate-100">
                                      {primaryEntity.displayName || "—"}
                                    </dd>
                                    <dt className="text-slate-500">UUID</dt>
                                    <dd className="break-all font-mono text-xs text-slate-700 dark:text-slate-200">{primaryEntity.id || "—"}</dd>
                                  </dl>
                                </div>
                                <div className="border-t border-slate-200 pt-4 dark:border-white/10">
                                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Uitgevoerd door</div>
                                  <dl className="mt-2 grid gap-2 sm:grid-cols-[140px_1fr]">
                                    <dt className="text-slate-500">Naam</dt>
                                    <dd className="font-semibold text-slate-900 dark:text-slate-100">{actorName || "—"}</dd>
                                    <dt className="text-slate-500">E-mail</dt>
                                    <dd className="text-slate-700 dark:text-slate-200">{entry?.email || "—"}</dd>
                                  </dl>
                                </div>
                                <div className="border-t border-slate-200 pt-4 dark:border-white/10">
                                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Actie</div>
                                  <div className="mt-2 font-semibold text-slate-900 dark:text-slate-100">
                                    {destructivePresentation?.actionLabel}
                                  </div>
                                </div>
                                {affectedEntities.length > 0 ? (
                                <div className="border-t border-slate-200 pt-4 dark:border-white/10">
                                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Betrokken entiteiten</div>
                                  {affectedEntities.length > 0 ? (
                                    <ul className="mt-2 space-y-2">
                                      {affectedEntities.map((entity, entityIndex) => (
                                        <li key={`${entity.type}-${entity.id}-${entityIndex}`} className="flex flex-wrap gap-2">
                                          <span className="font-semibold text-slate-900 dark:text-slate-100">
                                            {entity.displayName || entityTypeLabel(entity.type)}
                                          </span>
                                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-200">
                                            {entityTypeLabel(entity.type)}
                                          </span>
                                          <span className="font-mono text-xs text-slate-500">{entity.id}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : null}
                                </div>
                                ) : null}
                                <div className="border-t border-slate-200 pt-4 dark:border-white/10">
                                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Technische details</div>
                                  <dl className="mt-2 grid gap-2 sm:grid-cols-[140px_1fr]">
                                    <dt className="text-slate-500">HTTP method</dt><dd className="font-mono">{entry?.method || requestDetails?.method || "—"}</dd>
                                    <dt className="text-slate-500">Request path</dt><dd className="break-all font-mono text-xs">{entry?.path || requestDetails?.path || "—"}</dd>
                                    <dt className="text-slate-500">HTTP status</dt><dd>{entry?.status ?? "—"}</dd>
                                    <dt className="text-slate-500">IP</dt><dd className="font-mono text-xs">{entry?.ip || "—"}</dd>
                                    <dt className="text-slate-500">User UUID</dt><dd className="break-all font-mono text-xs">{actorId || "—"}</dd>
                                    <dt className="text-slate-500">Audit action</dt><dd className="font-mono text-xs">{action || "—"}</dd>
                                    <dt className="text-slate-500">Timestamp</dt><dd>{formatDateTime(when, i18n.language) || "—"}</dd>
                                  </dl>
                                </div>
                              </div>
                            ) : null}

                            {isExpanded && !isDestructiveAudit && diffRows.length > 0 ? (
                              <div className="ml-0 mt-4 overflow-hidden rounded-[14px] border border-slate-200/80 bg-white dark:border-white/10 dark:bg-slate-950/30 lg:ml-[262px]">
                                {diffRows.map((row, rowIdx) => {
                                  const FieldIcon = fieldIconFor(row.field);
                                  const isDeleteAction =
                                    String(action || "").toLowerCase().startsWith("delete") ||
                                    String(action || "").toLowerCase() === "remove";
                                  const showPrev = row.prev !== undefined && (row.next !== undefined || isDeleteAction);
                                  const showNext =
                                    row.next !== undefined ||
                                    (!isDeleteAction && row.prev !== undefined && row.next === undefined);
                                  const nextValue = row.next !== undefined ? row.next : row.prev;
                                  return (
                                    <div
                                      key={`${key}-${row.field}-${rowIdx}`}
                                      className="grid gap-3 border-b border-slate-200/80 px-4 py-3 text-sm last:border-0 dark:border-white/10 md:grid-cols-[180px_minmax(0,1fr)_24px_minmax(0,1fr)]"
                                    >
                                      <div className="flex items-center gap-3 font-semibold text-slate-700 dark:text-slate-200">
                                        <FieldIcon className="text-slate-500" aria-hidden="true" />
                                        {row.field}
                                      </div>
                                      <div className="min-w-0 text-slate-500 line-through dark:text-slate-400">
                                        {showPrev ? (
                                          <ExpandableText
                                            text={normalizeChangeValue(row.prev)}
                                            idKey={`${key}|${row.field}|${rowIdx}|prev`}
                                            maxChars={120}
                                          />
                                        ) : (
                                          "—"
                                        )}
                                      </div>
                                      <div className="text-center font-bold text-slate-500">→</div>
                                      <div className="min-w-0 font-semibold text-slate-900 dark:text-slate-100">
                                        {showNext ? (
                                          <ExpandableText
                                            text={normalizeChangeValue(nextValue)}
                                            idKey={`${key}|${row.field}|${rowIdx}|next`}
                                            maxChars={160}
                                          />
                                        ) : (
                                          "—"
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                                {recordId ? (
                                  <div className="flex items-center justify-end gap-2 px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
                                    {t("admin.logs.record", { defaultValue: "Record" })}: {recordId}
                                    <FaCopy aria-hidden="true" />
                                  </div>
                                ) : null}
                              </div>
                            ) : isExpanded && !isDestructiveAudit ? (
                              (metaText || usageDetails) && (
                                <div className="mt-4 rounded-[14px] bg-slate-50 p-3 text-xs text-slate-700 dark:bg-slate-900/60 dark:text-slate-200">
                                  <ExpandableText
                                    text={metaText || usageDetails}
                                    idKey={`${key}|metaText`}
                                    maxChars={900}
                                  />
                                </div>
                              )
                            ) : null}
                          </article>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
	            )}
	            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-slate-200/80 pt-4 dark:border-white/10">
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  {visibleLogs.length > 0
                    ? `${pageMeta.page * pageMeta.size + 1}-${pageMeta.page * pageMeta.size + visibleLogs.length} van ${pageMeta.totalElements} ${totalLabel}`
                    : `0 van ${pageMeta.totalElements} ${totalLabel}`}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => fetchLogs({ page: pageMeta.page - 1 })}
                    disabled={pageMeta.page <= 0 || refreshing}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-50 text-slate-300 dark:bg-slate-900/40"
                    aria-label={t("common.previous", { defaultValue: "Vorige" })}
                  >
                    ‹
                  </button>
                  <span className="px-3 text-sm font-semibold text-slate-600 dark:text-slate-200">
                    {pageMeta.page + 1} / {Math.max(pageMeta.totalPages, 1)}
                  </span>
                  <button
                    type="button"
                    onClick={() => hasMore && fetchLogs({ page: pageMeta.page + 1 })}
                    disabled={!hasMore || refreshing}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:text-indigo-700 disabled:opacity-50 dark:border-white/10 dark:bg-slate-950/30 dark:text-slate-200"
                    aria-label={t("common.next", { defaultValue: "Volgende" })}
                  >
                    ›
                  </button>
                </div>
	            </div>
	          </div>
	        )}
	      </div>
	      <Modal
	        show={confirmDelete.show}
	        onClose={() => (deleting ? null : setConfirmDelete({ show: false, entry: null }))}
	        title={t("admin.logs.delete_title", { defaultValue: "Logregel verwijderen" })}
	        hideDefaultActions
	      >
	        <div className="text-left space-y-4">
	          <div className="rounded-xl border border-rose-200 bg-rose-50/70 p-3 text-sm text-rose-800 dark:border-rose-500/40 dark:bg-rose-900/30 dark:text-rose-100">
	            <div className="font-extrabold">
	              {t("admin.logs.delete_warning", { defaultValue: "Let op: verwijderen is definitief." })}
	            </div>
	            <div className="mt-1">
	              {t("admin.logs.delete_logged", {
	                defaultValue: "Deze actie wordt ook gelogd (wie, wanneer en welke logregel).",
	              })}
	            </div>
	          </div>

	          {confirmDelete.entry && (
	            <div className="rounded-xl border border-gray-200 bg-white/70 p-3 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-100">
	              <div className="flex flex-wrap items-center justify-between gap-2">
	                <div className="font-semibold">
	                  {String(confirmDelete.entry?.action || "").toUpperCase() || t("common.unknown", { defaultValue: "Onbekend" })}
	                </div>
	                <div className="text-xs text-gray-500 dark:text-gray-400">
	                  {formatDateTime(confirmDelete.entry?.dateTime || confirmDelete.entry?.timestamp || confirmDelete.entry?.time, i18n.language) ||
	                    t("common.unknown", { defaultValue: "Onbekend" })}
	                </div>
	              </div>
	              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
	                {t("admin.logs.delete_id", { defaultValue: "Id" })}: {getLogDeleteId(confirmDelete.entry) || "—"}
	              </div>
	            </div>
	          )}

	          <div className="pt-2 flex flex-col gap-3 sm:flex-row sm:justify-end">
	            <button
	              type="button"
	              onClick={() => setConfirmDelete({ show: false, entry: null })}
	              disabled={deleting}
	              className="px-4 py-2 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60"
	            >
	              {t("common.cancel", { defaultValue: "Annuleren" })}
	            </button>
	            <button
	              type="button"
	              onClick={async () => {
	                const entry = confirmDelete.entry;
	                await performDeleteLogEntry(entry);
	                setConfirmDelete({ show: false, entry: null });
	              }}
	              disabled={deleting || !getLogDeleteId(confirmDelete.entry)}
	              className="px-4 py-2 rounded bg-rose-600 text-white hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-400 disabled:opacity-60"
	            >
	              {deleting
	                ? t("common.deleting", { defaultValue: "Verwijderen…" })
	                : t("common.actions.delete", { defaultValue: "Verwijderen" })}
	            </button>
	          </div>
	        </div>
	      </Modal>
	    </LdsOverviewPage>
	  );
	};

export default AdminLogs;
